<template>
  <b-modal
    id="export-modal"
    title="Export to Excel - Select Options"
    title-class="font-18"
    size="xl"
    scrollable
    @ok="handleExport"
    @cancel="handleCancel"
    ok-title="Export to Excel"
    cancel-title="Cancel"
  >
    <div v-if="isExporting" class="text-center py-5">
      <b-spinner variant="primary" label="Generating Excel file..."></b-spinner>
      <p class="mt-3">Generating Excel file...</p>
    </div>

    <div v-else>
      <!-- Sheet Selection -->
      <div class="mb-4">
        <h5 class="mb-3">Select Sheets to Export:</h5>
        <div class="row">
          <div class="col-md-4">
            <b-form-checkbox
              v-model="selectedSheets.times"
              :disabled="!tableData || tableData.length === 0"
            >
              <strong>Times</strong>
              <span v-if="tableData && tableData.length > 0"
                >({{ tableData.length }} records)</span
              >
              <span v-else class="text-muted">(No data)</span>
            </b-form-checkbox>
          </div>
          <div class="col-md-4">
            <b-form-checkbox
              v-model="selectedSheets.products"
              :disabled="
                !productsTableData || productsTableData.length === 0
              "
            >
              <strong>Products</strong>
              <span
                v-if="productsTableData && productsTableData.length > 0"
                >({{ productsTableData.length }} records)</span
              >
              <span v-else class="text-muted">(No data)</span>
            </b-form-checkbox>
          </div>
          <div class="col-md-4">
            <b-form-checkbox
              v-model="selectedSheets.procurements"
              :disabled="
                !procurementTableData || procurementTableData.length === 0
              "
            >
              <strong>Procurements</strong>
              <span
                v-if="
                  procurementTableData && procurementTableData.length > 0
                "
                >({{ procurementTableData.length }} records)</span
              >
              <span v-else class="text-muted">(No data)</span>
            </b-form-checkbox>
          </div>
        </div>
      </div>

      <hr />

      <!-- Times Sheet Options -->
      <div v-if="selectedSheets.times" class="mb-4">
        <div
          class="d-flex justify-content-between align-items-center mb-2"
          @click="showTimesOptions = !showTimesOptions"
          style="cursor: pointer"
        >
          <h5 class="mb-0">
            <i
              :class="
                showTimesOptions ? 'bx bx-chevron-down' : 'bx bx-chevron-right'
              "
            ></i>
            Times Sheet Options
          </h5>
        </div>
        <b-collapse v-model="showTimesOptions">
          <div class="ml-4 mb-3">
            <div class="d-flex justify-content-between mb-2">
              <strong>Select Fields:</strong>
              <div>
                <b-button size="sm" variant="outline-primary" class="mr-2" @click="selectAllTimesFields"
                  >Select All</b-button
                >
                <b-button size="sm" variant="outline-secondary" @click="deselectAllTimesFields"
                  >Deselect All</b-button
                >
              </div>
            </div>
            <div class="row">
              <div
                class="col-md-4"
                v-for="(value, field) in selectedTimesFields"
                :key="field"
              >
                <b-form-checkbox v-model="selectedTimesFields[field]">
                  {{ formatFieldName(field) }}
                </b-form-checkbox>
              </div>
            </div>
            <hr class="my-3" />
            <b-form-checkbox
              v-model="includeEmployeeTotalsForTimes"
              class="mt-2"
            >
              <strong>Include employee totals at top of sheet</strong>
              <small class="text-muted d-block"
                >Shows total hours, billable hours, and non-billable hours for
                each employee</small
              >
            </b-form-checkbox>
          </div>
        </b-collapse>
      </div>

      <!-- Products Sheet Options -->
      <div v-if="selectedSheets.products" class="mb-4">
        <div
          class="d-flex justify-content-between align-items-center mb-2"
          @click="showProductsOptions = !showProductsOptions"
          style="cursor: pointer"
        >
          <h5 class="mb-0">
            <i
              :class="
                showProductsOptions
                  ? 'bx bx-chevron-down'
                  : 'bx bx-chevron-right'
              "
            ></i>
            Products Sheet Options
          </h5>
        </div>
        <b-collapse v-model="showProductsOptions">
          <div class="ml-4 mb-3">
            <div class="d-flex justify-content-between mb-2">
              <strong>Select Fields:</strong>
              <div>
                <b-button size="sm" variant="outline-primary" class="mr-2" @click="selectAllProductsFields"
                  >Select All</b-button
                >
                <b-button size="sm" variant="outline-secondary" @click="deselectAllProductsFields"
                  >Deselect All</b-button
                >
              </div>
            </div>
            <div class="row">
              <div
                class="col-md-4"
                v-for="(value, field) in selectedProductsFields"
                :key="field"
              >
                <b-form-checkbox v-model="selectedProductsFields[field]">
                  {{ formatFieldName(field) }}
                </b-form-checkbox>
              </div>
            </div>
            <hr class="my-3" />
            <b-form-checkbox
              v-model="includeEmployeeTotalsForProducts"
              class="mt-2"
            >
              <strong>Include employee totals at top of sheet</strong>
              <small class="text-muted d-block"
                >Shows product quantity and revenue totals for each employee,
                grouped by product</small
              >
            </b-form-checkbox>
          </div>
        </b-collapse>
      </div>

      <!-- Procurements Sheet Options -->
      <div v-if="selectedSheets.procurements" class="mb-4">
        <div
          class="d-flex justify-content-between align-items-center mb-2"
          @click="showProcurementsOptions = !showProcurementsOptions"
          style="cursor: pointer"
        >
          <h5 class="mb-0">
            <i
              :class="
                showProcurementsOptions
                  ? 'bx bx-chevron-down'
                  : 'bx bx-chevron-right'
              "
            ></i>
            Procurements Sheet Options
          </h5>
        </div>
        <b-collapse v-model="showProcurementsOptions">
          <div class="ml-4 mb-3">
            <div class="d-flex justify-content-between mb-2">
              <strong>Select Fields:</strong>
              <div>
                <b-button size="sm" variant="outline-primary" class="mr-2" @click="selectAllProcurementsFields"
                  >Select All</b-button
                >
                <b-button size="sm" variant="outline-secondary" @click="deselectAllProcurementsFields"
                  >Deselect All</b-button
                >
              </div>
            </div>
            <div class="row">
              <div
                class="col-md-4"
                v-for="(value, field) in selectedProcurementsFields"
                :key="field"
              >
                <b-form-checkbox
                  v-model="selectedProcurementsFields[field]"
                >
                  {{ formatFieldName(field) }}
                </b-form-checkbox>
              </div>
            </div>
            <hr class="my-3" />
            <b-form-checkbox
              v-model="includeCustomerTotalsForProcurements"
              class="mt-2"
            >
              <strong>Include customer totals at top of sheet</strong>
              <small class="text-muted d-block"
                >Shows quoted amount, received amount, revenue, and profit
                totals for each customer</small
              >
            </b-form-checkbox>
          </div>
        </b-collapse>
      </div>

      <!-- Validation Message -->
      <div v-if="!hasSelectedSheets" class="alert alert-warning">
        <i class="bx bx-error-circle"></i> Please select at least one sheet
        to export.
      </div>
    </div>
  </b-modal>
</template>

<script>
import Vue from 'vue';
import * as XLSX from 'xlsx';
import moment from 'moment';
import VueToast from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
Vue.use(VueToast);

export default {
  name: 'ExportToCsvModal',
  props: {
    tableData: Array,
    productsTableData: Array,
    procurementTableData: Array,
    allEmployees: Array,
    searchCustomer: String,
    searchProduct: String,
    searchJobNum: Number,
    searchStartDate: Date,
    searchEndDate: Date,
    searchEmployee: String,
  },
  data() {
    return {
      isExporting: false,
      selectedSheets: {
        times: false,
        products: false,
        procurements: false,
      },
      selectedTimesFields: {
        approved: true,
        approvedBy: true,
        approvedTime: true,
        Employee: true,
        Date: true,
        Customer: true,
        Job: true,
        Description: true,
        Billable: true,
        Hours: true,
        Type: true,
        Rate: true,
        PayrollRate: true,
      },
      selectedProductsFields: {
        Approved: true,
        ApprovedBy: true,
        ApprovedTime: true,
        Date: true,
        Employee: true,
        Product: true,
        Quantity: true,
        Rate: true,
        Job: true,
        Customer: true,
        PurchasePrice: true,
        Billable: true,
      },
      selectedProcurementsFields: {
        date: true,
        customer: true,
        jobNum: true,
        name: true,
        quotedAmount: true,
        receivedAmount: true,
        markup: true,
        type: true,
        ordered: true,
        received: true,
        invoiced: true,
        billable: true,
      },
      includeEmployeeTotalsForTimes: true,
      includeEmployeeTotalsForProducts: true,
      includeCustomerTotalsForProcurements: true,
      showTimesOptions: true,
      showProductsOptions: true,
      showProcurementsOptions: true,
    };
  },
  computed: {
    hasSelectedSheets() {
      return (
        this.selectedSheets.times ||
        this.selectedSheets.products ||
        this.selectedSheets.procurements
      );
    },
  },
  methods: {
    selectAllTimesFields() {
      Object.keys(this.selectedTimesFields).forEach((field) => {
        this.selectedTimesFields[field] = true;
      });
    },
    deselectAllTimesFields() {
      Object.keys(this.selectedTimesFields).forEach((field) => {
        this.selectedTimesFields[field] = false;
      });
    },
    selectAllProductsFields() {
      Object.keys(this.selectedProductsFields).forEach((field) => {
        this.selectedProductsFields[field] = true;
      });
    },
    deselectAllProductsFields() {
      Object.keys(this.selectedProductsFields).forEach((field) => {
        this.selectedProductsFields[field] = false;
      });
    },
    selectAllProcurementsFields() {
      Object.keys(this.selectedProcurementsFields).forEach((field) => {
        this.selectedProcurementsFields[field] = true;
      });
    },
    deselectAllProcurementsFields() {
      Object.keys(this.selectedProcurementsFields).forEach((field) => {
        this.selectedProcurementsFields[field] = false;
      });
    },
    getEmployeeNameFromUid(uid) {
      if (!this.allEmployees || !uid) {
        return '';
      }
      const employee = this.allEmployees.find((emp) => emp.uid === uid);
      return employee ? employee.name : '';
    },
    formatFieldName(fieldName) {
      // Add space before capital letters, then capitalize first letter of each word
      return fieldName
        .replace(/([A-Z])/g, ' $1')  // Add space before capitals
        .replace(/^./, str => str.toUpperCase())  // Capitalize first letter
        .trim();
    },

    // Employee Totals Calculation for Times
    calculateTimesEmployeeTotals() {
      const totals = {};
      this.tableData.forEach((row) => {
        if (!totals[row.Employee]) {
          totals[row.Employee] = {
            'Employee Name': row.Employee,
            'Total Hours': 0,
            'Billable Hours': 0,
            'Non-Billable Hours': 0,
          };
        }
        const hours = Number(row.Hours) || 0;
        totals[row.Employee]['Total Hours'] += hours;
        if (row.Billable) {
          totals[row.Employee]['Billable Hours'] += hours;
        } else {
          totals[row.Employee]['Non-Billable Hours'] += hours;
        }
      });
      return Object.values(totals);
    },

    // Employee Totals Calculation for Products
    calculateProductsEmployeeTotals() {
      const totals = {};
      this.productsTableData.forEach((row) => {
        const key = `${row.Employee}|${row.Product}`;
        if (!totals[key]) {
          totals[key] = {
            'Employee Name': row.Employee,
            Product: row.Product,
            'Total Quantity': 0,
            'Total ($)': 0,
          };
        }
        totals[key]['Total Quantity'] += Number(row.Quantity) || 0;
        // Calculate revenue and store in 'Total ($)' column
        if (row.Billable) {
          totals[key]['Total ($)'] +=
            (Number(row.Quantity) || 0) * (Number(row.Rate) || 0);
        }
      });
      // Sort by employee name ascending
      return Object.values(totals).sort((a, b) => {
        return a['Employee Name'].localeCompare(b['Employee Name']);
      });
    },

    // Customer Totals Calculation for Procurements
    calculateProcurementsCustomerTotals() {
      const totals = {};
      this.procurementTableData.forEach((row) => {
        if (!totals[row.customer]) {
          totals[row.customer] = {
            Customer: row.customer,
            'Total Quoted': 0,
            'Total Received': 0,
            'Total Markup': 0,
            'Total Profit': 0,
          };
        }
        totals[row.customer]['Total Quoted'] +=
          Number(row.quotedAmount) || 0;
        totals[row.customer]['Total Received'] +=
          Number(row.receivedAmount) || 0;

        // Calculate revenue based on markup type
        let revenue = 0;
        if (row.billable && row.billable.value !== true) {
          if (row.markupType === 'percentage') {
            revenue =
              Number(row.receivedAmount) *
              (Number(row.markup) / 100 + 1);
          } else if (row.markupType === 'dollar') {
            revenue =
              Number(row.receivedAmount) + Number(row.markup);
          } else if (row.markupType === 'flatRate') {
            revenue = Number(row.markup);
          } else {
            revenue = Number(row.receivedAmount);
          }
        }
        totals[row.customer]['Total Markup'] += revenue;
        totals[row.customer]['Total Profit'] +=
          revenue - (Number(row.receivedAmount) || 0);
      });
      return Object.values(totals);
    },

    // Data Preparation - Filter based on selected fields and EXCLUDE 'id'
    prepareTimesData() {
      return this.tableData.map((row) => {
        const filtered = {};
        for (const field in this.selectedTimesFields) {
          // Skip id field - never include it
          if (field === 'id') continue;

          if (this.selectedTimesFields[field]) {
            // Format the field name for the Excel column header
            const formattedFieldName = this.formatFieldName(field);

            // Handle special field formatting
            if (field === 'approvedBy') {
              if (row[field]) {
                if (typeof row[field] === 'object') {
                  if (row[field].uid) {
                    filtered[formattedFieldName] = this.getEmployeeNameFromUid(row[field].uid);
                  } else {
                    filtered[formattedFieldName] = '';
                  }
                } else if (typeof row[field] === 'string') {
                  filtered[formattedFieldName] = row[field];
                } else {
                  filtered[formattedFieldName] = '';
                }
              } else {
                filtered[formattedFieldName] = '';
              }
            } else if (field === 'approvedTime' && row[field]) {
              filtered[formattedFieldName] = moment(new Date(row[field])).format('MMM DD, YY');
            } else {
              filtered[formattedFieldName] = row[field];
            }
          }
        }
        return filtered;
      });
    },

    prepareProductsData() {
      return this.productsTableData.map((row) => {
        const filtered = {};
        for (const field in this.selectedProductsFields) {
          // Skip id field - never include it
          if (field === 'id') continue;

          if (this.selectedProductsFields[field]) {
            // Format the field name for the Excel column header
            const formattedFieldName = this.formatFieldName(field);

            // Handle special field formatting
            if (field === 'ApprovedBy') {
              // Handle ApprovedBy field - extract name from object or use string directly
              if (row[field]) {
                if (typeof row[field] === 'object') {
                  // Try to use name if available
                  if (row[field].uid) {
                    filtered[formattedFieldName] = this.getEmployeeNameFromUid(row[field].uid);
                  } else {
                    filtered[formattedFieldName] = '';
                  }
                } else if (typeof row[field] === 'string') {
                  filtered[formattedFieldName] = row[field];
                } else {
                  filtered[formattedFieldName] = '';
                }
              } else {
                filtered[formattedFieldName] = '';
              }
            } else if (field === 'ApprovedTime' && row[field]) {
              filtered[formattedFieldName] = moment(new Date(row[field])).format('MMM DD, YY');
            } else {
              filtered[formattedFieldName] = row[field];
            }
          }
        }
        return filtered;
      });
    },

    prepareProcurementsData() {
      return this.procurementTableData.map((row) => {
        const filtered = {};
        for (const field in this.selectedProcurementsFields) {
          // Skip id field - never include it
          if (field === 'id') continue;

          if (this.selectedProcurementsFields[field]) {
            // Format the field name for the Excel column header
            const formattedFieldName = this.formatFieldName(field);

            // Handle special field formatting
            if (field === 'date' && row[field]) {
              filtered[formattedFieldName] = moment(new Date(row[field])).format('MMM DD, YY');
            } else if (field === 'ordered' && row[field]) {
              filtered[formattedFieldName] = row[field].value ? 'Yes' : 'No';
            } else if (field === 'received' && row[field]) {
              filtered[formattedFieldName] = row[field].value ? 'Yes' : 'No';
            } else if (field === 'invoiced' && row[field]) {
              filtered[formattedFieldName] = row[field].value ? 'Yes' : 'No';
            } else if (field === 'billable' && row[field]) {
              filtered[formattedFieldName] = row[field].value !== true ? 'Yes' : 'No';
            } else {
              filtered[formattedFieldName] = row[field];
            }
          }
        }
        return filtered;
      });
    },

    // Format numeric Excel sheet cells
    formatNumericExcelSheetCells(cellArray) {
      for (const cell in cellArray) {
        let cellValue = cellArray[cell].v;
        if (
          !isNaN(cellValue) &&
          typeof cellValue !== 'boolean' &&
          typeof cellValue !== 'undefined' &&
          typeof cellValue !== 'object'
        ) {
          cellArray[cell].t = 'n';
        }
      }
      return cellArray;
    },

    // Style worksheet for professional appearance
    styleWorksheet(worksheet) {
      const range = XLSX.utils.decode_range(worksheet['!ref']);

      // Column widths
      const colWidths = [];
      for (let C = range.s.c; C <= range.e.c; ++C) {
        let maxWidth = 10;
        for (let R = range.s.r; R <= range.e.r; ++R) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          const cell = worksheet[cellAddress];
          if (cell && cell.v) {
            const cellLength = String(cell.v).length;
            maxWidth = Math.max(maxWidth, Math.min(cellLength, 50));
          }
        }
        colWidths.push({ wch: maxWidth + 2 });
      }
      worksheet['!cols'] = colWidths;

      return worksheet;
    },

    // Add title rows to worksheet with sheet name and date range
    addTitleToWorksheet(worksheet, sheetName) {
      // Get the date range for display
      const startDate = this.searchStartDate
        ? moment(this.searchStartDate).format('MMM DD, YYYY')
        : 'N/A';
      const endDate = this.searchEndDate
        ? moment(this.searchEndDate).format('MMM DD, YYYY')
        : 'N/A';
      const dateRange = `Date Range: ${startDate} - ${endDate}`;

      // Get current range
      const range = XLSX.utils.decode_range(worksheet['!ref']);

      // Shift all existing rows down by 3 (to make room for title, date range, and blank row)
      const newCells = {};
      for (let R = range.e.r; R >= range.s.r; R--) {
        for (let C = range.s.c; C <= range.e.c; C++) {
          const oldAddress = XLSX.utils.encode_cell({ r: R, c: C });
          const newAddress = XLSX.utils.encode_cell({ r: R + 3, c: C });
          if (worksheet[oldAddress]) {
            newCells[newAddress] = worksheet[oldAddress];
            delete worksheet[oldAddress];
          }
        }
      }

      // Apply the shifted cells
      Object.assign(worksheet, newCells);

      // Add title in first row
      worksheet['A1'] = {
        v: sheetName.toUpperCase(),
        t: 's'
      };

      // Add date range in second row
      worksheet['A2'] = {
        v: dateRange,
        t: 's'
      };

      // Update the range to include the new rows
      worksheet['!ref'] = XLSX.utils.encode_range({
        s: { r: 0, c: range.s.c },
        e: { r: range.e.r + 3, c: range.e.c }
      });

      return worksheet;
    },

    // Generate file name
    generateFileName() {
      const currentDate = moment().format('YYYY-MM-DD');
      return `Cost Earnings Report - ${currentDate}.xlsx`;
    },

    // Main export function
    async handleExport(bvModalEvent) {
      // Prevent modal from closing
      bvModalEvent.preventDefault();

      // Validate that at least one sheet is selected
      if (!this.hasSelectedSheets) {
        Vue.$toast.open({
          message: 'Please select at least one sheet to export',
          type: 'warning',
          duration: 4000,
          position: 'top-right',
        });
        return;
      }

      this.isExporting = true;

      try {
        const workbook = XLSX.utils.book_new();

        // 1. Times Sheet
        if (
          this.selectedSheets.times &&
          this.tableData &&
          this.tableData.length > 0
        ) {
          const timesData = this.prepareTimesData();
          const worksheet = XLSX.utils.json_to_sheet(timesData);
          this.formatNumericExcelSheetCells(worksheet);
          this.addTitleToWorksheet(worksheet, 'Times');
          this.styleWorksheet(worksheet);
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Times');

          // Times Totals Sheet (separate)
          if (this.includeEmployeeTotalsForTimes) {
            const totalsRows = this.calculateTimesEmployeeTotals();
            const totalsWorksheet = XLSX.utils.json_to_sheet(totalsRows);
            this.formatNumericExcelSheetCells(totalsWorksheet);
            this.addTitleToWorksheet(totalsWorksheet, 'Times Totals');
            this.styleWorksheet(totalsWorksheet);
            XLSX.utils.book_append_sheet(workbook, totalsWorksheet, 'Times Totals');
          }
        }

        // 2. Products Sheet
        if (
          this.selectedSheets.products &&
          this.productsTableData &&
          this.productsTableData.length > 0
        ) {
          const productsData = this.prepareProductsData();
          const worksheet = XLSX.utils.json_to_sheet(productsData);
          this.formatNumericExcelSheetCells(worksheet);
          this.addTitleToWorksheet(worksheet, 'Products');
          this.styleWorksheet(worksheet);
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Products');

          // Products Totals Sheet (separate)
          if (this.includeEmployeeTotalsForProducts) {
            const totalsRows = this.calculateProductsEmployeeTotals();
            const totalsWorksheet = XLSX.utils.json_to_sheet(totalsRows);
            this.formatNumericExcelSheetCells(totalsWorksheet);
            this.addTitleToWorksheet(totalsWorksheet, 'Products Totals');
            this.styleWorksheet(totalsWorksheet);
            XLSX.utils.book_append_sheet(workbook, totalsWorksheet, 'Products Totals');
          }
        }

        // 3. Procurements Sheet
        if (
          this.selectedSheets.procurements &&
          this.procurementTableData &&
          this.procurementTableData.length > 0
        ) {
          const procurementsData = this.prepareProcurementsData();
          const worksheet = XLSX.utils.json_to_sheet(procurementsData);
          this.formatNumericExcelSheetCells(worksheet);
          this.addTitleToWorksheet(worksheet, 'Procurements');
          this.styleWorksheet(worksheet);
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Procurements');

          // Procurements Totals Sheet (separate)
          if (this.includeCustomerTotalsForProcurements) {
            const totalsRows = this.calculateProcurementsCustomerTotals();
            const totalsWorksheet = XLSX.utils.json_to_sheet(totalsRows);
            this.formatNumericExcelSheetCells(totalsWorksheet);
            this.addTitleToWorksheet(totalsWorksheet, 'Procurements Totals');
            this.styleWorksheet(totalsWorksheet);
            XLSX.utils.book_append_sheet(workbook, totalsWorksheet, 'Procurements Totals');
          }
        }

        // Download file
        const fileName = this.generateFileName();
        XLSX.writeFile(workbook, fileName);

        Vue.$toast.open({
          message: 'Excel file exported successfully!',
          type: 'success',
          duration: 4000,
          position: 'top-right',
        });

        // Close modal after successful export
        this.$nextTick(() => {
          this.$bvModal.hide('export-modal');
        });
      } catch (error) {
        console.error('Error exporting to Excel:', error);
        Vue.$toast.open({
          message: `Error exporting to Excel: ${error.message}`,
          type: 'error',
          duration: 6000,
          position: 'top-right',
        });
      } finally {
        this.isExporting = false;
      }
    },

    handleCancel() {
      // Reset to defaults when cancelled
      this.showTimesOptions = true;
      this.showProductsOptions = true;
      this.showProcurementsOptions = true;
    },

    // Initialize sheet selection based on data availability
    initializeSheetSelection() {
      this.selectedSheets.times = this.tableData && this.tableData.length > 0;
      this.selectedSheets.products = this.productsTableData && this.productsTableData.length > 0;
      this.selectedSheets.procurements = this.procurementTableData && this.procurementTableData.length > 0;
    },
  },
  mounted() {
    // Initialize selectedSheets based on data availability
    this.initializeSheetSelection();
  },
  watch: {
    tableData() {
      this.initializeSheetSelection();
    },
    productsTableData() {
      this.initializeSheetSelection();
    },
    procurementTableData() {
      this.initializeSheetSelection();
    },
  },
};
</script>

<style scoped>
.alert {
  margin-bottom: 0;
}
</style>

<style>
/* Remove green highlighting from disabled checkboxes in this modal only */
#export-modal .custom-control-input:disabled ~ .custom-control-label {
  background-color: #fff !important;
  opacity: 0.6;
}

/* Also ensure the label itself has white background when disabled */
#export-modal .custom-control-input:disabled ~ .custom-control-label::before {
  background-color: #fff !important;
}
</style>
